import React from 'react';
import styled from 'styled-components';
import { IAudience } from '@contensis/personalization';
import { Icons, IconsDictionary } from '@/components/icon/Icon.types';

const AudienceCardStyled = styled.div`
  display: flex;
  padding: 1em 0.25em 1em 0.5em;
  align-items: flex-start;
  gap: 1em;

  border-bottom: 1px solid var(--color-border-low-contrast, #f0f0f0);
  background: var(--color-background-surface, #fff);

  &.disabled {
    text-decoration: line-through;
    background: var(--color-border-low-contrast, #f0f0f0);
    color: var(--color-border-high-contrast, #949494);
    border-radius: 0.5em;
    margin-bottom: 0.25em;
  }
`;

const AudienceCardContentStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;

  .name {
    align-self: stretch;
    color: var(--color-text-high-contrast, #444);

    /* Bold/text-preset-6--bold */
    font-size: 0.875em;
    font-style: normal;
    font-weight: 700;
    line-height: 1.5em; /* 171.429% */
  }

  .description {
    color: var(--color-text-low-contrast, #595959);

    /* Normal/text-preset-6 */
    font-size: 0.875em;
    font-style: normal;
    font-weight: 400;
    line-height: 1.5em; /* 171.429% */
  }
`;

const AudienceCardActionStyled = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5em;

  div {
    position: relative;

    button {
      padding: unset;
      background: none;

      width: 2em;
      height: 2em;
      border: 2px solid transparent;
      border-radius: 0.1875em;
      display: flex;
      justify-content: center;
      align-items: center;

      .focus-ring {
        position: absolute;
        width: 2.25em;
        height: 2.25em;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    }

    button:hover {
      background: var(--color-neutral-20, #f0f0f0);
      border: 2px solid var(--color-border-mid-contrast, #dbdbdb);
    }
    button:active {
      border: 2px solid var(--color-border-high-contrast, #949494);
    }
    button:focus {
      box-shadow: none !important;
      outline: none !important;
      .focus-ring {
        border: 2px solid var(--a11y);
        border-radius: 0.1875em;
      }
    }
  }
`;

const EyeIcon = Icons[IconsDictionary.Eye];
const RemoveIcon = Icons[IconsDictionary.Remove];

const AudienceCard = ({
  audience,
  isActive,
  isDisabled,
  isPreview,
  toggleAudience,
  removeAudience,
}: {
  audience: IAudience;
  isActive: boolean;
  isDisabled: boolean;
  isPreview: boolean;
  toggleAudience: (id: string) => void;
  removeAudience: (id: string) => void;
}) => {
  return (
    <AudienceCardStyled
      className={isDisabled ? 'disabled' : ''}
      title={
        isDisabled
          ? `This audience was not found in the ${isPreview ? 'manifest preview' : 'published manifest'}`
          : undefined
      }
    >
      <AudienceCardContentStyled>
        <div className="name">{audience.name}</div>
        <div className="description">{audience.description}</div>
      </AudienceCardContentStyled>
      <AudienceCardActionStyled>
        <div>
          <button
            data-id="panel-btn"
            onClick={() => toggleAudience(audience.id)}
          >
            <span className="focus-ring" />
            <EyeIcon state={Number(isActive)} />
            <span className="sr-only">{isActive ? 'Disable' : 'Enable'}</span>
          </button>
        </div>
        <div>
          <button
            data-id="panel-btn"
            onClick={() => removeAudience(audience.id)}
          >
            <span className="focus-ring" />
            <RemoveIcon />
            <span className="sr-only">Remove</span>
          </button>
        </div>
      </AudienceCardActionStyled>
    </AudienceCardStyled>
  );
};

export default AudienceCard;
